import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: string;
  items: FAQItem[];
}

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggleFAQ = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  const scrollToSection = (id: string) => {
    window.location.href = `/#${id}`;
  };

  const faqCategories: FAQCategory[] = [
    {
      title: 'General Questions',
      icon: 'ri-question-line',
      items: [
        {
          question: 'What exactly is Providence Path, and who do you help?',
          answer: 'Providence Path is a specialized business growth partner focused exclusively on helping small to mid-sized HVAC companies overcome the most common obstacles to sustainable growth. We implement proven strategies in marketing, lead generation, reputation management, and operational systems to transform feast-or-famine businesses into predictable, scalable enterprises.'
        },
        {
          question: 'Why do you focus only on HVAC businesses?',
          answer: 'By specializing in HVAC, we understand the seasonal demand fluctuations, the regulatory landscape, the technician shortage, and the specific customer journey in your industry better than general marketing or consulting firms. This deep-niche focus allows us to provide highly effective, tailored solutions that deliver faster and better results.'
        },
        {
          question: 'How is Providence Path different from a typical marketing agency?',
          answer: 'A typical marketing agency focuses on one service (like running ads or building a website). Providence Path provides a holistic system. We don\'t just generate leads; we address the entire path—from getting you visible, ensuring your reputation is stellar, pre-qualifying the leads, and creating the internal systems that allow you to handle the growth without burning out.'
        }
      ]
    },
    {
      title: 'Results & Strategy',
      icon: 'ri-line-chart-line',
      items: [
        {
          question: 'Your solution mentions "Strategic local visibility optimization." What does that actually mean?',
          answer: 'It means we focus on getting your business to appear prominently where your ideal customers are searching right now. This includes optimizing your Google Business Profile (GBP), employing targeted Local SEO strategies, and ensuring your name, address, and phone number (NAP) are consistent across all major online directories. The goal is to make you invisible to local customers no more.'
        },
        {
          question: 'How quickly will I start seeing better leads and results?',
          answer: 'While results vary based on your current standing, we typically see noticeable improvements in call quality and volume within the first 60 to 90 days as we implement the foundations of local optimization and targeted attraction strategies. Our goal is predictable growth, not overnight miracles.'
        },
        {
          question: 'How do you solve the "Leads That Never Convert" problem?',
          answer: 'We implement targeted attraction strategies by clearly defining and communicating your ideal customer profile and service area across all platforms. This pre-qualifies prospects before they even call. We also help you implement better internal phone scripting and process training to ensure your team can effectively convert the high-quality leads we deliver.'
        },
        {
          question: 'I am constantly "Competing on Price Alone." How can you help me command premium pricing?',
          answer: 'We achieve this through Value-Based Positioning. We help you clearly articulate the unique value (e.g., exceptional warranties, guaranteed response times, top-tier technician training) that justifies a higher price point. Your modern digital presence and stream of 5-star reviews provide the social proof that makes customers gladly choose your value over a competitor\'s low price.'
        }
      ]
    },
    {
      title: 'Implementation & Operations',
      icon: 'ri-tools-line',
      items: [
        {
          question: 'My biggest challenge is "Can\'t Scale Beyond Myself." Do you offer operational help?',
          answer: 'Absolutely. While we focus heavily on attraction, we also provide Proven Systems and Processes for the back end. This includes guidance on automating review requests, standardizing customer communication, and establishing clear roles/responsibilities so that excellent service delivery is system-dependent, not owner-dependent.'
        },
        {
          question: 'How do you help us attract quality technicians?',
          answer: 'We implement Employer Branding Strategies. We leverage your positive customer reviews and company culture to showcase why you are the best employer in the area. This involves optimizing your careers page, leveraging social media to highlight team success, and developing a reputation that attracts and retains the top-tier talent you need to scale confidently.'
        },
        {
          question: 'What is the typical process for getting started with Providence Path?',
          answer: 'It begins with a Strategic Growth Audit of your current business—your digital presence, lead sources, conversion rates, and internal systems. From there, we present a customized "Providence Path" blueprint detailing the specific strategies and timelines we will execute to address your core pain points and achieve your growth goals.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0F3A2E] to-[#0A2A20] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#C9A24D] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#C9A24D] rounded-2xl mb-6 shadow-2xl">
            <i className="ri-question-answer-line text-4xl text-[#0F3A2E]"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about how Providence Path helps HVAC businesses achieve sustainable, predictable growth
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-16 last:mb-0">
              {/* Category Header */}
              <div className="flex items-center mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-[#1F4D3A] to-[#183F30] rounded-xl flex items-center justify-center shadow-lg mr-4">
                  <i className={`${category.icon} text-2xl text-[#C9A24D]`}></i>
                </div>
                <h2 className="text-3xl font-bold text-[#0F3A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {category.title}
                </h2>
              </div>

              {/* FAQ Items */}
              <div className="space-y-4">
                {category.items.map((item, itemIndex) => {
                  const key = `${categoryIndex}-${itemIndex}`;
                  const isOpen = openIndex === key;

                  return (
                    <div
                      key={itemIndex}
                      className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
                    >
                      <button
                        onClick={() => toggleFAQ(categoryIndex, itemIndex)}
                        className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer group"
                      >
                        <span className="text-lg font-semibold text-[#0F3A2E] pr-8 group-hover:text-[#1F4D3A] transition-colors">
                          {item.question}
                        </span>
                        <div className={`w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-[#C9A24D]/10 ${isOpen ? 'rotate-180' : ''}`}>
                          <i className={`ri-arrow-down-s-line text-2xl ${isOpen ? 'text-[#C9A24D]' : 'text-[#0F3A2E]'}`}></i>
                        </div>
                      </button>
                      
                      <div
                        className={`transition-all duration-300 ease-in-out ${
                          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <div className="px-8 pb-6 pt-2">
                          <div className="h-px bg-gradient-to-r from-[#C9A24D]/20 via-[#C9A24D]/40 to-[#C9A24D]/20 mb-4"></div>
                          <p className="text-base text-gray-700 leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F3A2E] to-[#0A2A20] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 bg-[#C9A24D] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-teal-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Still Have Questions?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            We're here to help. Schedule a free discovery call to discuss your specific situation and see how we can help your business thrive.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://calendly.com/providencepath"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-[#C9A24D] text-[#0F3A2E] font-bold rounded-full hover:bg-[#B8963E] transition-all whitespace-nowrap cursor-pointer shadow-2xl transform hover:scale-105"
              style={{ boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.1)' }}
            >
              Schedule Free Call
            </a>
            <a
              href="mailto:Providencepath@pm.me"
              className="inline-block px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer border border-white/20"
            >
              Email Us Instead
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
